import { useEffect, useId, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

export interface FormModalProps {
  open: boolean
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'danger' | 'default'
  loading?: boolean
  disabled?: boolean
  onConfirm: () => void
  onCancel: () => void
  children: ReactNode
}

/**
 * Modal with form fields for edit / close / partial-close flows.
 */
export function FormModal({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'default',
  loading = false,
  disabled = false,
  onConfirm,
  onCancel,
  children,
}: FormModalProps) {
  const { t } = useTranslation('common')
  const titleId = useId()
  const descId = useId()
  const firstRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const prev = document.activeElement as HTMLElement | null
    const focusable = firstRef.current?.querySelector<HTMLElement>(
      'input, textarea, select, button',
    )
    focusable?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
      prev?.focus?.()
    }
  }, [open, onCancel])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-[2px]"
        aria-label={cancelLabel ?? t('actions.cancel')}
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        className={cn(
          'relative w-full max-w-md rounded-lg border border-terminal-border',
          'bg-terminal-surface p-5 shadow-2xl',
          'animate-[modal-in_180ms_ease-out]',
        )}
      >
        <h2 id={titleId} className="text-base font-semibold text-terminal-fg">
          {title}
        </h2>
        {description ? (
          <p
            id={descId}
            className="mt-1.5 text-sm leading-relaxed text-terminal-muted"
          >
            {description}
          </p>
        ) : null}
        <div ref={firstRef} className="mt-4 space-y-3">
          {children}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className={cn(
              'h-9 min-w-20 rounded px-3 text-sm font-medium',
              'border border-terminal-border text-terminal-fg hover:bg-terminal-elevated',
              'disabled:opacity-50',
            )}
          >
            {cancelLabel ?? t('actions.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || disabled}
            className={cn(
              'h-9 min-w-20 rounded px-3 text-sm font-semibold text-white',
              'disabled:opacity-50',
              tone === 'danger'
                ? 'bg-danger hover:bg-danger-muted'
                : 'bg-accent hover:bg-accent-muted',
            )}
          >
            {loading
              ? t('actions.working')
              : (confirmLabel ?? t('actions.confirm'))}
          </button>
        </div>
      </div>
    </div>
  )
}

export function ModalField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
      {label}
      {children}
    </label>
  )
}

export const modalInputClass = cn(
  'h-9 w-full rounded-md border border-terminal-border/50 bg-terminal-bg px-2.5',
  'font-mono text-xs tabular-nums text-terminal-fg',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
)
